import React from 'react'
import { useAddClearanceSetup, ClearanceSetupFormValues } from '@modmd/data'
import Button from 'components/Button'
import Card from 'components/Card'
import { Checkbox } from 'components/Checkbox'
import { Box } from 'components/Layout'
import { NumberInput } from 'components/NumberInput'
import { Text } from 'components/Typography'
import { FormikProps } from 'formik'

type FormClearanceSettingProps = {
  onClose: () => void
  onSubmit: () => void
  id: string
  clearanceForm: FormikProps<ClearanceSetupFormValues>
}

export const FormClearanceSetting = ({
  onClose,
  id,
  onSubmit,
  clearanceForm,
}: FormClearanceSettingProps) => {
  const [addClearanceSetup] = useAddClearanceSetup({
    onCompleted: () => {},
  })

  const submitClearanceSettings = async () => {
    await addClearanceSetup({
      variables: {
        inputData: {
          companyId: id,
          isTestOn: clearanceForm.values.isTestOn,
          ...(clearanceForm.values.isTestOn && {
            amountOfDaysPositive: Number(clearanceForm.values.amountOfDaysPositive),
            amountOfDaysNegative: Number(clearanceForm.values.amountOfDaysNegative),
            amountOfDaysSinceLastPositive: Number(
              clearanceForm.values.amountOfDaysSinceLastPositive
            ),
          }),
          isQuestionnaireOn: clearanceForm.values.isQuestionnaireOn,
          ...(clearanceForm.values.isQuestionnaireOn && {
            isTravelRelevant: clearanceForm.values.isTravelRelevant,
            isExposureOn: clearanceForm.values.isExposureOn,
            isSymptomOn: clearanceForm.values.isSymptomOn,
            isTemperatureOn: clearanceForm.values.isTemperatureOn,
          }),
          isVaccinationOn: clearanceForm.values.isVaccinationOn,
        },
      },
    })
    onClose()
    onSubmit()
  }

  return (
    <Card.Content>
      <Checkbox
        checked={clearanceForm.values.isTestOn}
        onChange={() => {
          void clearanceForm.setFieldValue('isTestOn', !clearanceForm.values.isTestOn)
        }}
        label="Tests should be considered"
      />
      <Box paddingLeft="2rem" display="flex" flexDirection="column" height="83px" mb="1rem">
        <NumberInput
          name="amountOfDaysPositive"
          label=" Amount of days after last positive is not cleared"
          onChange={(e) => {
            void clearanceForm.setFieldValue('amountOfDaysPositive', e.target.value)
          }}
          value={!clearanceForm.values.isTestOn ? '' : clearanceForm.values.amountOfDaysPositive}
          disabled={!clearanceForm.values.isTestOn}
        />
        <NumberInput
          name="amountOfDaysNegative"
          label="Amount of days since last negative is cleared"
          onChange={(e) => {
            void clearanceForm.setFieldValue('amountOfDaysNegative', e.target.value)
          }}
          value={!clearanceForm.values.isTestOn ? '' : clearanceForm.values.amountOfDaysNegative}
          disabled={!clearanceForm.values.isTestOn}
        />
        <NumberInput
          name="amountOfDaysSinceLastPositive"
          label="Amount of days since last positive but no trace of virus is cleared"
          onChange={(e) => {
            void clearanceForm.setFieldValue('amountOfDaysSinceLastPositive', e.target.value)
          }}
          value={
            !clearanceForm.values.isTestOn ? '' : clearanceForm.values.amountOfDaysSinceLastPositive
          }
          disabled={!clearanceForm.values.isTestOn}
        />
      </Box>
      <Checkbox
        checked={clearanceForm.values.isQuestionnaireOn}
        onChange={() =>
          void clearanceForm.setValues({
            ...clearanceForm.values,
            isTravelRelevant: clearanceForm.values.isQuestionnaireOn
              ? false
              : !clearanceForm.values.isTravelRelevant,
            isExposureOn: clearanceForm.values.isQuestionnaireOn
              ? false
              : !clearanceForm.values.isExposureOn,
            isSymptomOn: clearanceForm.values.isQuestionnaireOn
              ? false
              : !clearanceForm.values.isExposureOn,
            isTemperatureOn: clearanceForm.values.isQuestionnaireOn
              ? false
              : !clearanceForm.values.isTemperatureOn,
            isQuestionnaireOn: !clearanceForm.values.isQuestionnaireOn,
          })
        }
        label={<Text>Questionnaire should be considered</Text>}
      />
      <Box paddingLeft="2rem">
        <Checkbox
          checked={
            !clearanceForm.values.isQuestionnaireOn ? false : clearanceForm.values.isTravelRelevant
          }
          onChange={(e) => {
            void clearanceForm.setFieldValue('isTravelRelevant', e.target.checked)
          }}
          label="Travel restriction"
          disabled={!clearanceForm.values.isQuestionnaireOn}
        />
        <Checkbox
          checked={
            !clearanceForm.values.isQuestionnaireOn ? false : clearanceForm.values.isExposureOn
          }
          onChange={(e) => {
            void clearanceForm.setFieldValue('isExposureOn', e.target.checked)
          }}
          label="Exposure Restriction"
          disabled={!clearanceForm.values.isQuestionnaireOn}
        />
        <Checkbox
          checked={
            !clearanceForm.values.isQuestionnaireOn ? false : clearanceForm.values.isSymptomOn
          }
          onChange={(e) => {
            void clearanceForm.setFieldValue('isSymptomOn', e.target.checked)
          }}
          label="Symptoms Restriction"
          disabled={!clearanceForm.values.isQuestionnaireOn}
        />
        <Checkbox
          checked={
            !clearanceForm.values.isQuestionnaireOn ? false : clearanceForm.values.isTemperatureOn
          }
          onChange={(e) => {
            void clearanceForm.setFieldValue('isTemperatureOn', e.target.checked)
          }}
          label="Temperature Restriction"
          disabled={!clearanceForm.values.isQuestionnaireOn}
        />
      </Box>
      <Box>
        <Checkbox
          checked={clearanceForm.values?.isVaccinationOn}
          onChange={(e) => void clearanceForm.setFieldValue('isVaccinationOn', e.target.checked)}
          label="Vaccines should be considered"
        />
      </Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Button colorVariant="secondary" mr="0.25rem" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={submitClearanceSettings}>Save Settings</Button>
      </Box>
    </Card.Content>
  )
}
